// app/routes/_layout.dispensary-locations.$location/route.tsx

// =============================================================================
// DISPENSARY LOCATION PAGE - REFACTORED & CLEANED UP
// =============================================================================
//
// ⚠️  BACKEND IMPLEMENTATION NEEDED - READ THIS FIRST ⚠️
//
// This file has been refactored and cleaned up, but currently uses MOCK DATA for several features.
// The following backend systems need to be implemented for full functionality:
//
// 1. REVIEWS SYSTEM:
//    - Real customer reviews database with CRUD operations
//    - Review verification system (Verified Member badges)
//    - Store-specific review aggregation and display
//    - Review moderation and approval workflow
//
// 2. REVIEW INTERACTIONS:
//    - Like/dislike functionality with user authentication
//    - Share review system with social media integration
//    - Review helpfulness tracking and analytics
//    - User interaction history and preventing duplicate votes
//
// 3. STORE RATINGS:
//    - Dynamic store ratings based on actual review data
//    - Real-time rating calculations (currently hardcoded to 4.5 stars)
//    - Review count aggregation per store location
//    - Rating distribution analytics
//
// 4. API ENDPOINTS NEEDED:
//    - GET /api/reviews/:storeId - Fetch reviews for specific store
//    - POST /api/reviews/:reviewId/like - Like/unlike review
//    - POST /api/reviews/:reviewId/dislike - Dislike/remove dislike
//    - POST /api/reviews/:reviewId/share - Track review shares
//    - GET /api/stores/:storeId/rating - Get current store rating
//
// 5. DATABASE TABLES NEEDED:
//    - reviews (id, store_id, user_id, rating, title, content, verified, created_at)
//    - review_interactions (id, review_id, user_id, type, created_at)
//    - store_ratings (id, store_id, average_rating, total_reviews, updated_at)
//
// CURRENT MOCK DATA LOCATIONS:
// - defaultReviewData in ./static-data.ts (replace with API calls)
// - Review interactions in component state (replace with backend persistence)
// - Hardcoded 4.5 star ratings (replace with dynamic calculation)
//
// =============================================================================
// This page displays individual dispensary location details including:
// - Location info (address, hours, phone)
// - Image gallery
// - Local deals carousel
// - Customer reviews
// - Nearby dispensaries
// - FAQ section
//
// Structure:
// 1. Imports & Types
// 2. Data & Configuration
// 3. Meta & Loader Functions
// 4. Main Component with Sections
// 5. Mobile/Desktop Layout Rendering
// =============================================================================

// IMPORTS
import FaqsSection from './components/faqs-section';
import DispensaryNearMePage, {
  DUMMY_NEAR_ME_PAGE,
  type NearMePageData,
} from '~/components/dispensary-near-me-page';
import { fetchCatalystNearMeBySlug } from '~/utils/nearby-dispensaries.server';
import { fetchCarousels } from '~/api/promotional-carousel.server';
import { getSelectedStore } from '~/cookies';
import type { Carousel } from '~/types/promotional-carousel';
import { supabase } from '~/utils/db-utils.server';

// Inject a `title` attribute into an iframe HTML snippet if one isn't already
// present (WCAG 2.4.1 — every iframe needs an accessible name). Map embeds in
// website_locations.map_embed sometimes ship without one.
function ensureIframeTitle(html: string, title: string): string {
  if (!html) return html;
  if (/<iframe\b[^>]*\btitle\s*=/i.test(html)) return html;
  return html.replace(/<iframe\b/i, `<iframe title="${title.replace(/"/g, '&quot;')}"`);
}

// Phone numbers for the 8 destination Catalyst stores that near-me pages can match.
// Phones aren't stored in Supabase (only in the `stores` array on this route),
// so we surface them via this map when rendering the near-me sidebar.
const NEAR_ME_STORE_PHONES: Record<string, string> = {
  'san-diego-dispensary': '(858) 277-4200',
  'pomona-dispensary': '(909) 766-4200',
  'bellflower-dispensary': '(562) 925-5323',
  'lancaster-dispensary': '(661) 945-4200',
  'el-monte-dispensary': '(626) 350-4200',
  'oxnard-dispensary': '(805) 385-4200',
  'cherry-ave-long-beach-dispensary': '(562) 439-0420',
  'retro-row-long-beach-dispensary': '(562) 612-4420',
  'hawthorne-dispensary': '(310) 970-4200',
};
import { resolveImageUrl, fetchCentralHoursByStore, resolveOpeningHours } from '~/utils/locations.server';
import { getStoresFromCentral } from '~/api/store-central.server';
import { generateLocationSchema } from '~/utils/schema-utils';
import {
  useLoaderData,
  useSearchParams,
  Link,
  useNavigate,
  useRouteError,
  isRouteErrorResponse,
  useParams,
} from '@remix-run/react';
import type { MetaFunction, LoaderFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
// Removed ProductProvider, ProductGrid, and ProductsLoaderData imports since this is now an information-only page
import { useEffect, useRef, useState, startTransition } from 'react';
import { useStore } from '~/context/store-context';
import { generateTokenForStore } from '~/api/skunkworks';
import { useStickyWithinContainer } from '~/hooks/use-sticky-within-container';
import { getStoreHoursOverride } from '~/utils/store-hours-override';
import Breadcrumbs from '~/components/breadcrumbs';
import { locationMeta, defaultImages, defaultReviewData, getDefaultFaqData } from './static-data';
import {
  getZipCode,
  calculateZipDistance,
  getDispensaryImage,
  getGalleryImages,
  extractLocationData,
} from './helper-functions';
import { socialImageMeta } from '~/utils/social-meta';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

interface Store {
  name: string;
  value: string;
  address: string;
  hours: string;
  url: string;
  phone?: string;
}

const stores: Store[] = [
  {
    name: 'ANTIOCH',
    value: 'catalystantioch',
    address: '2625 Crow Ct, Antioch, CA 94509',
    url: '/dispensary-locations/antioch-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(925) 756-7140',
  },
  {
    name: 'BELLFLOWER',
    value: 'catalystbellflower',
    address: '9032 Artesia Blvd, Bellflower, CA 90706',
    url: '/dispensary-locations/bellflower-dispensary',
    hours: 'Open 9am - 9pm',
    phone: '(562) 925-5323',
  },
  {
    name: 'BELMONT SHORE',
    value: 'catalystbelmont',
    address: '5227 E 2nd St, Long Beach, CA 90803',
    url: '/dispensary-locations/belmont-shore-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(562) 439-0420',
  },
  {
    name: 'CHERRY',
    value: 'catalyst405',
    address: '3170 Cherry Ave, Long Beach, CA 90807',
    url: '/dispensary-locations/cherry-ave-long-beach-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(562) 427-5323',
  },
  {
    name: 'COSTA MESA',
    value: 'catalystcostamesa',
    address: '170 E 17th St Ste 115, Costa Mesa, CA 92627',
    url: '/dispensary-locations/costa-mesa-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(714) 754-4200',
  },
  {
    name: 'DALY CITY',
    value: 'catalystdalycity',
    address: '1000 King Dr Ste 200, Daly City, CA 94015',
    url: '/dispensary-locations/daly-city-dispensary',
    hours: 'Open 10am - 9.30pm',
    phone: '(650) 301-4420',
  },
  {
    name: 'DOWNTOWN LONG BEACH',
    value: 'catalystpine',
    address: '433 Pine Street, Ste 500, Long Beach, CA 90802',
    url: '/dispensary-locations/downtown-long-beach-dispensary',
    hours: 'Open 10.30am - 9.30pm',
    phone: '(562) 612-4420',
  },
  {
    name: 'EL MONTE',
    value: 'catalystelmonte',
    address: '12154 Valley Blvd, El Monte, CA 91732',
    url: '/dispensary-locations/el-monte-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(626) 350-4200',
  },
  {
    name: 'FIGUEROA',
    value: 'catalystfigueroa',
    address: '10020 S Figueroa St, Los Angeles, CA 90003',
    url: '/dispensary-locations/figueroa-los-angeles-dispensary',
    hours: 'Open 10am - 9.30pm',
    phone: '(323) 750-4200',
  },
  {
    name: 'FLORENCE',
    value: 'catalystflorence',
    address: '316 W Florence Avenue, Los Angeles, CA 90003',
    url: '/dispensary-locations/florence-los-angeles-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(323) 751-4200',
  },
  {
    name: 'GROVER BEACH',
    value: 'catalystgroverbeach',
    address: '1053 Highland Way, Grover Beach, CA 93433',
    url: '/dispensary-locations/grover-beach-dispensary',
    hours: 'Open 9am - 9pm',
    phone: '(805) 904-4200',
  },
  {
    name: 'HAWTHORNE',
    value: 'catalysthawthorne',
    address: '14115 Crenshaw BLVD #9, Hawthorne, CA 90250',
    url: '/dispensary-locations/hawthorne-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(310) 970-4200',
  },
  {
    name: 'HEMET',
    value: 'catalysthemet',
    address: '41007 E Florida Ave, Hemet, CA 92544',
    url: '/dispensary-locations/hemet-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(951) 929-4200',
  },
  {
    name: 'LANCASTER',
    value: 'catalystlancaster',
    address: '42020 4th St E, Lancaster, CA 93535',
    url: '/dispensary-locations/lancaster-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(661) 945-4200',
  },
  {
    name: 'LYNWOOD',
    value: 'catalystlynwood',
    address: '5110 E Imperial Hwy, Lynwood, CA 90262',
    url: '/dispensary-locations/lynwood-dispensary',
    hours: 'Open 9am-9:30pm',
    phone: '(310) 898-4200',
  },
  {
    name: 'MARINA',
    value: 'catalystmarina',
    address: '3100 Del Monte Blvd, Marina, CA 93933',
    url: '/dispensary-locations/marina-dispensary',
    hours: 'Open 10am - 9.30pm',
    phone: '(831) 384-4200',
  },
  {
    name: 'MID CITY',
    value: 'catalystmidcity',
    address: '3318 Venice Blvd, Los Angeles, CA 90019',
    url: '/dispensary-locations/mid-city-los-angeles-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(323) 732-4200',
  },
  {
    name: 'MORENO VALLEY',
    value: 'catalystmorenovalley',
    address: '24703 Alessandro Blvd, Moreno Valley, CA 92553',
    url: '/dispensary-locations/moreno-valley-dispensary',
    hours: 'Open 10am - 9.30pm',
    phone: '(951) 243-4200',
  },
  {
    name: 'NORMANDIE',
    value: 'catalystnormandie',
    address: '8300 S Normandie Ave, Los Angeles, CA 90044',
    url: '/dispensary-locations/normandie-los-angeles-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(323) 750-4201',
  },
  {
    name: 'OC3',
    value: 'oc3',
    address: '3122 S Halladay St Unit A, Santa Ana, CA 92705',
    url: '/dispensary-locations/oc3-santa-ana-dispensary',
    hours: 'Open 9am-9:30pm',
    phone: '(714) 542-4200',
  },
  {
    name: 'OXNARD',
    value: 'catalystoxnard',
    address: '4749 S Rose Ave, Oxnard, CA 93033',
    url: '/dispensary-locations/oxnard-dispensary',
    hours: 'Open 9am - 9pm',
    phone: '(805) 385-4200',
  },
  {
    name: 'PALM DESERT',
    value: 'catalystpalmdesert',
    address: '39420 Berkey Dr, Palm Desert, CA 92211',
    url: '/dispensary-locations/palm-desert-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(760) 346-4200',
  },
  {
    name: 'PATTERSON',
    value: 'catalystpatterson',
    address: '100 W Las Palmas Ave, Patterson, CA 95363',
    url: '/dispensary-locations/patterson-dispensary',
    hours: 'Open 10am - 9pm',
    phone: '(209) 892-4200',
  },
  {
    name: 'POMONA',
    value: 'catalystpomona',
    address: '456 E Holt Ave, Pomona, CA 91767',
    url: '/dispensary-locations/pomona-dispensary',
    hours: 'Open 9am - 9.50pm',
    phone: '(909) 623-4200',
  },
  {
    name: 'PLANET CATALYST',
    value: 'catalystp13',
    address: '3400 W Warner Ave, Santa Ana, CA 92704',
    url: '/dispensary-locations/planet-catalyst-orange-county',
    hours: 'Mon-Thu 9am - 9.30pm | Fri-Sun 8am - 9.30pm',
    phone: '(562) 732-1313',
  },
  {
    name: 'SANTA ANA',
    value: 'catalystsantaana',
    address: '2400 Pullman St, Santa Ana, CA 92705',
    url: '/dispensary-locations/santa-ana-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(714) 542-4201',
  },
  {
    name: 'SAN BERNARDINO',
    value: 'catalystsbinland',
    address: '506 Inland Center Dr, San Bernardino, CA 92408',
    url: '/dispensary-locations/san-bernardino-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(909) 890-4200',
  },

  {
    name: 'RETRO ROW',
    value: 'catalystlift',
    address: '2800 E 4th St Long Beach, CA 90814',
    url: '/dispensary-locations/retro-row-long-beach-dispensary',
    hours: 'Open 12pm - 9.30pm',
    phone: '(562) 517-4420',
  },

  {
    name: 'SAN DIEGO',
    value: 'catalystsandiego',
    address: '5125 Convoy St, STE 211, San Diego, CA 92111',
    url: '/dispensary-locations/san-diego-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(858) 277-4200',
  },
  {
    name: 'SILVER LAKE',
    value: 'catalystsilverlake',
    address: '2334 Fletcher Dr, Los Angeles, CA 90039',
    url: '/dispensary-locations/silver-lake-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(323) 913-4200',
  },
  {
    name: 'STANTON',
    value: 'catalyststanton',
    address: '8130 Monroe Ave, Stanton, CA 90680',
    url: '/dispensary-locations/stanton-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(714) 484-4200',
  },
  {
    name: 'VAN NUYS',
    value: 'catalystvannuys',
    address: '8162 Orion Ave, Van Nuys, CA 91406',
    url: '/dispensary-locations/van-nuys-dispensary',
    hours: 'Open 10am - 9.30pm',
    phone: '(818) 787-4200',
  },
  {
    name: 'WATSONVILLE',
    value: 'catalystwatsonville',
    address: '1324 W Beach St, Watsonville, CA 95076',
    url: '/dispensary-locations/watsonville-dispensary',
    hours: 'Open 10am - 9pm',
    phone: '(831) 724-4200',
  },
];

// Define the types for our location data
interface LocationDetails {
  faqs?: { question: string; answer: string }[];
  headerTitle?: string;
  storeDetailPageIntro?: string;
  openingHours?: string;
  seoText?: string;
  dealsContent?: string;
  postReviewsContent?: string;
  address?: string;
  email?: string;
  phone?: string;
  directionsUrl?: string;
  mapEmbed?: string;
  reviewsWidget?: string;
  rating?: number;
  reviewCount?: number;
  comingSoon?: string[];
  addressmap?: {
    latitude: number;
    longitude: number;
  };
  storeImage?: {
    node: {
      mediaItemUrl: string;
      altText?: string;
    };
  };
  storeGallery?: {
    nodes: {
      mediaItemUrl: string;
      altText?: string;
    }[];
  };
}

interface Location {
  seo?: {
    metaDesc?: string;
    title?: string;
    opengraphTitle?: string;
  };
  headerTitle?: string;
  title?: string;
  slug?: string;
  content?: string;
  locationDetails?: LocationDetails;
}

interface CombinedLoaderData {
  location: Location | null;
  activeStore: string | null;
  /** Switched off in Store Central — the page shows, but can't be shopped. */
  isStoreInactive: boolean;
  /** Address pieces, coordinates and maps link from Store Central, for JSON-LD. */
  storeSchemaData?: {
    addressParts: { street: string; state: string; city: string; zip: string; county?: string } | null;
    geo: { lat: number; lng: number } | null;
    mapsUrl: string | null;
  } | null;
  allLocations: Array<{
    title: string;
    slug: string;
    locationDetails: {
      storeImage?: {
        node: {
          mediaItemUrl: string;
          altText?: string;
        };
      };
      address: string;
      addressmap?: {
        latitude: number;
        longitude: number;
      };
      openingHours: string;
      phone: string;
      email: string;
      comingSoon: string[];
    };
  }>;
}

// --- FULL META FUNCTION BASED ON YOUR SPREADSHEET ---

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
  // Near-me conquest pages: data shape is { kind, urlSlug, page }.
  if (data && (data as { kind?: string }).kind === 'near-me') {
    const { page, urlSlug } = data as {
      kind: 'near-me';
      urlSlug: string;
      page: NearMePageData;
    };
    const canonical = `https://catalyst-cannabis.com/dispensary-locations/${urlSlug}`;
    const title = page.seoTitle || 'Dispensary Near You | Catalyst Cannabis';
    const description =
      page.seoDescription ||
      'Find a top-rated Catalyst Cannabis dispensary near you. Premium cannabis at fair prices, with daily deals and online ordering.';
    const ogTitle = page.ogTitle || title;
    return [
      { title },
      { name: 'description', content: description },
      { tagName: 'link', rel: 'canonical', href: canonical },
      { property: 'og:title', content: ogTitle },
      { property: 'og:description', content: description },
      { property: 'og:url', content: canonical },
      { property: 'og:type', content: 'place' },
      { property: 'og:locale', content: 'en_US' },
      { property: 'og:site_name', content: 'Catalyst Cannabis Dispensaries' },
      { name: 'twitter:title', content: ogTitle },
      { name: 'twitter:description', content: description },
      { name: 'twitter:card', content: 'summary_large_image' },
      // These pages refer people to a store in a neighbouring city rather than
      // showing one of their own, so there is no page-specific photo to use —
      // the default card is the honest choice.
      ...socialImageMeta({ alt: `Catalyst Cannabis near ${page.city}` }),
      {
        name: 'robots',
        content:
          'follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large',
      },
    ];
  }

  if (!data) {
    return [
      {
        title: 'Your Favorite Cannabis Dispensary | Catalyst Cannabis Dispensaries',
      },
      {
        name: 'description',
        content:
          'Shop the best cannabis products at Catalyst Cannabis dispensaries in California. Find top-quality flower, vapes, edibles, concentrates, and more at unbeatable prices.',
      },
    ];
  }

  const slug = params.location?.toLowerCase();

  const metaData = locationMeta[slug as keyof typeof locationMeta];

  // Precedence: Budflow CMS → hardcoded fallback → generated template.
  //
  // The CMS values (`website_locations.seo_title` / `seo_description`, edited in
  // Budflow under Website > Locations > SEO) used to be loaded and then ignored,
  // because `locationMeta` was checked first. That made every title change a code
  // change — see Herb's 2026-08-12 request for Planet Catalyst, which prompted
  // this. All 33 locations have since been seeded into the CMS with the exact
  // values that were hardcoded, so flipping the order is a no-op for everything
  // except Planet Catalyst.
  //
  // `locationMeta` stays as a fallback for now so a blank CMS field can't wipe a
  // title. Once we're confident the CMS is the source of truth it can go.
  const cmsTitle = data.location?.seo?.title?.trim();
  const cmsDescription = data.location?.seo?.metaDesc?.trim();

  const finalTitle =
    cmsTitle ||
    metaData?.title ||
    `Your Favorite ${data.location?.title} Neighborhood Cannabis Dispensary | Catalyst Cannabis`;
  const description =
    cmsDescription ||
    metaData?.description ||
    `Shop the best cannabis products at Catalyst ${data.location?.title} Cannabis Dispensary in California.`;

  const canonicalUrl = `https://catalyst-cannabis.com/dispensary-locations/${slug}`;

  const latitude = data.location?.locationDetails?.addressmap?.latitude;
  const longitude = data.location?.locationDetails?.addressmap?.longitude;
  const city = data.location?.title;

  return [
    { title: finalTitle },
    { name: 'description', content: description },
    { tagName: 'link', rel: 'canonical', href: canonicalUrl },
    { property: 'og:title', content: finalTitle },
    { property: 'og:description', content: description },
    { property: 'og:url', content: canonicalUrl },
    { name: 'twitter:title', content: finalTitle },
    { name: 'twitter:description', content: description },
    // Robots directive
    { name: 'robots', content: 'follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large' },
    // OG type for locations
    { property: 'og:type', content: 'place' },
    { property: 'og:locale', content: 'en_US' },
    { property: 'og:site_name', content: 'Catalyst Cannabis Dispensaries' },
    // Geo meta tags for local SEO
    ...(latitude && longitude ? [
      { name: 'geo.position', content: `${latitude};${longitude}` },
      { name: 'geo.region', content: 'US-CA' },
      { name: 'ICBM', content: `${latitude}, ${longitude}` },
    ] : []),
    ...(city ? [{ name: 'geo.placename', content: city }] : []),
    // OG image - use store image if available
    ...socialImageMeta({
      image: data.location?.locationDetails?.storeImage?.node?.mediaItemUrl,
      alt: `${finalTitle}`,
    }),
    { name: 'twitter:card', content: 'summary_large_image' },
  ];
};

export const loader: LoaderFunction = async ({ request, params }) => {
  // Get the location slug from URL params
  const locationSlug = params.location;
  if (!locationSlug) {
    throw new Error('Location slug not found in params');
  }

  // "Dispensary Near [city]" SEO conquest pages share this URL family
  // (/dispensary-locations/near-<city>-ca). They render a separate template
  // sourced from `website_nearby_dispensaries` (brand='catalyst').
  //
  // URL → DB slug:  near-la-jolla-ca  →  la-jolla
  //   strip the constant `near-` prefix and `-ca` suffix; only the city
  //   portion is stored in the budflow admin.
  //
  // Use ?preview=1 on any /dispensary-locations/near-* URL to force the
  // hardcoded sample layout (useful before any rows exist in the DB).
  if (locationSlug.startsWith('near-')) {
    const url = new URL(request.url);
    if (url.searchParams.get('preview') === '1') {
      return json({
        kind: 'near-me',
        urlSlug: locationSlug,
        page: DUMMY_NEAR_ME_PAGE,
        dealsCarousel: null,
      });
    }

    const dbSlug = locationSlug.replace(/^near-/, '').replace(/-ca$/, '');
    // Fetch the near-me page and Store Central hours in parallel. Hours are
    // resolved HERE (in the loader), not inside fetchCatalystNearMeBySlug —
    // that function caches its whole result for 30 min, which would stale-bake
    // hours and defeat Store Central's read-time holiday-override freshness.
    const [result, centralHours, centralStores] = await Promise.all([
      fetchCatalystNearMeBySlug(dbSlug),
      fetchCentralHoursByStore(),
      // Same source the store pages use, so the two templates describe the
      // referred-to store identically rather than one rich and one thin.
      getStoresFromCentral(),
    ]);
    if (!result) {
      throw new Response('Near-me page not found', { status: 404 });
    }

    const { page: row, faqs, matchedLocation } = result;
    const matchedSlug = row.matched_location_slug || '';
    const matchedName = matchedLocation?.header_title || '';
    const phone = matchedSlug ? NEAR_ME_STORE_PHONES[matchedSlug] || '' : '';

    // The near-[city] page describes the store it refers people to — Cherry Ave
    // for near-cerritos-ca — never a store in that city, which does not exist.
    // Fabricating one would be a guidelines violation as well as a lie.
    const matchedCentral = centralStores.find(
      (entry) => entry.value === matchedLocation?.store_text_id,
    );
    const matchedCustom = matchedLocation?.custom_fields as
      | { latitude?: string | number; longitude?: string | number; email?: string }
      | null
      | undefined;

    const pageData: NearMePageData = {
      slug: row.slug,
      storeSchemaData: {
        addressParts: matchedCentral?.addressParts ?? null,
        geo:
          matchedCentral?.geo ??
          (matchedCustom?.latitude && matchedCustom?.longitude
            ? { lat: Number(matchedCustom.latitude), lng: Number(matchedCustom.longitude) }
            : null),
        mapsUrl: matchedCentral?.mapsUrl ?? null,
        email: matchedCustom?.email ?? null,
        ratingValue: matchedLocation?.star_rating ?? null,
        reviewCount: matchedLocation?.review_count ?? null,
      },
      city: (row.header || '').replace(/^Dispensary Near\s+/i, '').replace(/,?\s*CA\s*$/i, '') || row.slug,
      matchedLocationSlug: matchedSlug,
      matchedLocationName: matchedName,
      matchedLocationAddress: matchedLocation?.store_address || '',
      matchedLocationHours: resolveOpeningHours(
        centralHours,
        matchedLocation?.store_text_id,
        matchedLocation?.opening_hours_display,
        matchedLocation?.slug,
      ),
      matchedLocationPhone: phone,
      matchedLocationDirectionsUrl: matchedLocation?.directions_url || '',
      matchedLocationMapEmbed: ensureIframeTitle(
        matchedLocation?.map_embed || '',
        `Map showing Catalyst ${matchedName} location`,
      ),
      matchedLocationReviewsWidget: matchedLocation?.reviews_widget || '',
      seoTitle: row.seo_title || '',
      seoDescription: row.seo_description || '',
      ogTitle: row.og_title || row.seo_title || '',
      header: row.header || '',
      introText: row.intro_text || '',
      directionsHeader: row.directions_header || '',
      directionsContent: row.directions_content || '',
      box1Header: row.box1_header || '',
      box1Content: row.box1_content || '',
      box2Header: row.box2_header || '',
      box2Content: row.box2_content || '',
      box3Header: row.box3_header || '',
      box3Content: row.box3_content || '',
      box4Header: row.box4_header || '',
      box4Content: row.box4_content || '',
      faqs: faqs.map((f) => ({ question: f.question, answer: f.answer })),
    };

    // Promotional carousel for the deals slot. Fetched per-request with the
    // user's cookie-selected store (with the same fallback the homepage uses)
    // so the carousel always reflects the store the user shops with.
    // Lives outside `fetchCatalystNearMeBySlug` (which is slug-cached) because
    // carousel data must vary per store.
    const store = getSelectedStore(request) || 'catalystbellflower';
    const carousels: Record<string, Carousel | null> = await fetchCarousels(
      ['slot_j47o52'],
      store,
    ).catch(() => ({}));
    const dealsCarousel: Carousel | null = carousels['slot_j47o52'] ?? null;

    return json({
      kind: 'near-me',
      urlSlug: locationSlug,
      page: pageData,
      dealsCarousel,
    });
  }

  // Fetch this location + all Catalyst locations from Supabase, plus live hours
  // from Store Central — all in parallel. Hours are sourced from Store Central
  // (keyed by store_text_id) so the header and nearby cards reflect hour changes
  // made there; the stale opening_hours_display column is only a fallback.
  const [locationResult, allLocationsResult, centralHours, centralStores] = await Promise.all([
    supabase
      .from('website_locations')
      .select('*')
      .eq('brand', 'catalyst')
      .eq('slug', locationSlug)
      .single(),
    supabase
      .from('website_locations')
      .select('*')
      .eq('brand', 'catalyst'),
    fetchCentralHoursByStore(),
    // Store Central is the only source carrying the address already split into
    // its parts, the coordinates and the maps link. The `stores` array in this
    // file is a hardcoded fallback and has none of them, which is why the
    // PostalAddress below used to be derived by splitting a display string.
    getStoresFromCentral(),
  ]);

  const sbLoc = locationResult.data;
  if (!sbLoc) {
    throw new Response('Location not found', { status: 404 });
  }

  // A store switched off in Store Central drops out of getStoresFromCentral(),
  // which is what takes it out of the store selector. The page holds it to the
  // same rule: it stays up, but it can't be shopped or offered as a nearby store.
  // Every value in `stores` is one Store Central's safety net requires the API to
  // know, so a missing one means not Active rather than never heard of.
  const inactiveStoreValues = new Set(
    stores
      .filter((s) => !centralStores.some((entry) => entry.value === s.value))
      .map((s) => s.value),
  );

  // Fetch FAQs and gallery for this location
  const [faqsResult, galleryResult] = await Promise.all([
    supabase
      .from('website_location_faqs')
      .select('question, answer, sort_order')
      .eq('location_id', sbLoc.id)
      .order('sort_order'),
    supabase
      .from('website_location_gallery')
      .select('image_url, alt_text, sort_order')
      .eq('location_id', sbLoc.id)
      .order('sort_order'),
  ]);

  // Map Supabase data to the WordPress Location shape components expect
  const location: Location = {
    seo: {
      metaDesc: sbLoc.seo_description || undefined,
      title: sbLoc.seo_title || undefined,
      opengraphTitle: sbLoc.og_title || undefined,
    },
    title: sbLoc.custom_fields?.page_title || sbLoc.header_title || sbLoc.slug,
    slug: sbLoc.slug,
    content: undefined, // Keep empty — hero section falls through to storeDetailPageIntro; postReviewsContent renders separately
    locationDetails: {
      faqs: (faqsResult.data || []).map((f: any) => ({
        question: f.question,
        answer: f.answer,
      })),
      headerTitle: sbLoc.header_title || undefined,
      storeDetailPageIntro: sbLoc.intro_text || undefined,
      openingHours:
        resolveOpeningHours(
          centralHours,
          sbLoc.store_text_id,
          sbLoc.opening_hours_display,
          sbLoc.slug,
        ) || undefined,
      seoText: sbLoc.seo_text || undefined,
      dealsContent: sbLoc.custom_fields?.deals_content || undefined,
      postReviewsContent: sbLoc.content || undefined,
      address: sbLoc.custom_fields?.address || undefined,
      email: sbLoc.custom_fields?.email || undefined,
      phone: sbLoc.custom_fields?.phone || undefined,
      directionsUrl: sbLoc.directions_url || undefined,
      mapEmbed: sbLoc.map_embed || undefined,
      reviewsWidget: sbLoc.custom_fields?.reviews_widget || undefined,
      rating: sbLoc.star_rating || undefined,
      reviewCount: sbLoc.review_count || undefined,
      comingSoon: sbLoc.coming_soon ? ['Yes'] : ['No'],
      addressmap: sbLoc.custom_fields?.latitude && sbLoc.custom_fields?.longitude
        ? {
            latitude: Number(sbLoc.custom_fields.latitude),
            longitude: Number(sbLoc.custom_fields.longitude),
          }
        : undefined,
      storeImage: sbLoc.store_image_url
        ? {
            node: {
              mediaItemUrl: resolveImageUrl(sbLoc.store_image_url),
              altText: sbLoc.store_image_alt || '',
            },
          }
        : undefined,
      storeGallery: {
        nodes: (galleryResult.data || []).map((g: any) => ({
          mediaItemUrl: resolveImageUrl(g.image_url),
          altText: g.alt_text || '',
        })),
      },
    },
  };

  // Map all locations for nearby dispensaries section
  const allLocations = (allLocationsResult.data || [])
    .filter((loc: any) => !inactiveStoreValues.has(loc.store_text_id))
    .map((loc: any) => ({
    title: loc.custom_fields?.page_title || loc.header_title || loc.slug,
    slug: loc.slug,
    locationDetails: {
      storeImage: loc.store_image_url
        ? {
            node: {
              mediaItemUrl: resolveImageUrl(loc.store_image_url),
              altText: loc.store_image_alt || '',
            },
          }
        : undefined,
      address: loc.custom_fields?.address || '',
      addressmap: loc.custom_fields?.latitude && loc.custom_fields?.longitude
        ? {
            latitude: Number(loc.custom_fields.latitude),
            longitude: Number(loc.custom_fields.longitude),
          }
        : undefined,
      openingHours: resolveOpeningHours(
        centralHours,
        loc.store_text_id,
        loc.opening_hours_display,
        loc.slug,
      ),
      phone: loc.custom_fields?.phone || '',
      email: loc.custom_fields?.email || '',
      comingSoon: loc.coming_soon ? ['Yes'] : ['No'],
    },
  }));

  // --- Server-side Active Store Detection ---
  // Normalize the current pathname by removing trailing slashes.
  const url = new URL(request.url);
  const currentPath = url.pathname.replace(/\/+$/, '');

  // Special handling for Hawthorne Cannabis Lounge - it uses the same store as Hawthorne Dispensary
  let matchedStore = stores.find((store) => store.url.replace(/\/+$/, '') === currentPath);

  // If no match found and we're on the Hawthorne Lounge page, use Hawthorne store
  if (!matchedStore && currentPath === '/dispensary-locations/hawthorne-cannabis-lounge') {
    matchedStore = stores.find((store) => store.value === 'catalysthawthorne');
  }

  // Determine if this page/location is marked Coming Soon in WordPress
  // Special exception: Retro Row should NOT be treated as coming soon
  const isThisLocationComingSoon =
    location?.slug === 'retro-row-long-beach-dispensary'
      ? false // Force Retro Row to NOT be coming soon
      : Array.isArray(location?.locationDetails?.comingSoon)
        ? location.locationDetails.comingSoon.includes('Yes')
        : false;

  const isThisStoreInactive = !!matchedStore && inactiveStoreValues.has(matchedStore.value);
  // Neither kind of store may become the visitor's store from this page.
  const isThisLocationShoppable = !isThisLocationComingSoon && !isThisStoreInactive;

  // Check if the user is explicitly overriding the store selection
  // An inactive store is not honoured from the URL either.
  const requestedStore = url.searchParams.get('selectedStore');
  const userSelectedStore =
    requestedStore && !inactiveStoreValues.has(requestedStore) ? requestedStore : null;

  // Here we update the cookie on the server (if necessary) by reading the incoming cookie
  // and setting it if it doesn't match the matched store, unless user has explicitly selected a different store
  const cookieHeader = request.headers.get('Cookie') || '';
  let currentCookieValue = '';

  // Since we're not using a Remix cookie helper here, we assume the cookie is in document.cookie format.
  // For production, consider using createCookie.
  const cookieMatch = cookieHeader.split('; ').find((row) => row.startsWith('selectedStore='));
  if (cookieMatch) {
    currentCookieValue = cookieMatch.split('=')[1];
  }

  const headers = new Headers();

  // Only set the store cookie if there's no user override and the matched store is different from current
  if (
    matchedStore &&
    !userSelectedStore &&
    currentCookieValue !== matchedStore.value &&
    isThisLocationShoppable
  ) {
    // Set the cookie with a one-year expiration
    headers.append(
      'Set-Cookie',
      `selectedStore=${matchedStore.value}; path=/; max-age=${60 * 60 * 24 * 365}`,
    );
    currentCookieValue = matchedStore.value;
  } else if (userSelectedStore) {
    // If user explicitly selected a store, honor that choice
    headers.append(
      'Set-Cookie',
      `selectedStore=${userSelectedStore}; path=/; max-age=${60 * 60 * 24 * 365}`,
    );
    currentCookieValue = userSelectedStore;
  }

  // Pass the active store value from the server to the client
  const activeStore =
    userSelectedStore ||
    (isThisLocationShoppable && matchedStore ? matchedStore.value : currentCookieValue || null);

  // Return location data and active store (no product data needed)
  // Only the fields the JSON-LD needs, rather than the whole record: this is
  // serialised into the HTML on every location page.
  const centralStore = centralStores.find((entry) => entry.value === matchedStore?.value);
  const storeSchemaData = centralStore
    ? {
        addressParts: centralStore.addressParts ?? null,
        geo: centralStore.geo ?? null,
        mapsUrl: centralStore.mapsUrl ?? null,
      }
    : null;

  return json(
    {
      location,
      activeStore,
      isStoreInactive: isThisStoreInactive,
      allLocations,
      storeSchemaData,
    },
    { headers },
  );
};

export default function LocationsPage() {
  const loaderData = useLoaderData<
    | CombinedLoaderData
    | {
        kind: 'near-me';
        urlSlug: string;
        page: NearMePageData;
        dealsCarousel: Carousel | null;
      }
  >();

  // Near-me conquest pages share this URL family but render a different template.
  if ('kind' in loaderData && loaderData.kind === 'near-me') {
    return (
      <DispensaryNearMePage
        page={loaderData.page}
        dealsCarousel={loaderData.dealsCarousel}
      />
    );
  }

  const { location, activeStore, isStoreInactive, allLocations, storeSchemaData } =
    loaderData as CombinedLoaderData;
  const [searchParams] = useSearchParams();
  const params = useParams();
  const searchTerm = searchParams.get('search') || '';

  // Sticky sidebar functionality
  const { stickyRef: sidebarRef, containerRef: gridContainerRef } = useStickyWithinContainer({
    stickyOffset: 32,
    fixedWidth: 350,
    minScreenWidth: 1024,
  });

  // --- Initialize active store from loader data on first render ---
  const { selectedStore, setSelectedStore } = useStore();
  const navigate = useNavigate();

  // Review interaction state
  const [reviewInteractions, setReviewInteractions] = useState<
    Record<number, { likes: number; dislikes: number; userLiked: boolean; userDisliked: boolean }>
  >({
    0: { likes: 0, dislikes: 0, userLiked: false, userDisliked: false },
    1: { likes: 0, dislikes: 0, userLiked: false, userDisliked: false },
    2: { likes: 0, dislikes: 0, userLiked: false, userDisliked: false },
  });

  // Handle review like/dislike
  const handleReviewInteraction = (reviewIndex: number, type: 'like' | 'dislike') => {
    setReviewInteractions((prev) => {
      const current = prev[reviewIndex];
      const newInteraction = { ...current };

      if (type === 'like') {
        if (current.userLiked) {
          // Remove like
          newInteraction.likes = Math.max(0, current.likes - 1);
          newInteraction.userLiked = false;
        } else {
          // Add like (remove dislike if present)
          if (current.userDisliked) {
            newInteraction.dislikes = Math.max(0, current.dislikes - 1);
            newInteraction.userDisliked = false;
          }
          newInteraction.likes = current.likes + 1;
          newInteraction.userLiked = true;
        }
      } else {
        if (current.userDisliked) {
          // Remove dislike
          newInteraction.dislikes = Math.max(0, current.dislikes - 1);
          newInteraction.userDisliked = false;
        } else {
          // Add dislike (remove like if present)
          if (current.userLiked) {
            newInteraction.likes = Math.max(0, current.likes - 1);
            newInteraction.userLiked = false;
          }
          newInteraction.dislikes = current.dislikes + 1;
          newInteraction.userDisliked = true;
        }
      }

      return {
        ...prev,
        [reviewIndex]: newInteraction,
      };
    });
  };

  // Handle share review
  const handleShareReview = async (reviewTitle: string, reviewerName: string) => {
    const shareData = {
      title: `${reviewTitle} - Review by ${reviewerName}`,
      text: `Check out this review of ${locationName}: "${reviewTitle}" by ${reviewerName}`,
      url: window.location.href,
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        // Fallback to copying URL to clipboard
        await navigator.clipboard.writeText(window.location.href);
        // You could show a toast notification here
        alert('Review link copied to clipboard!');
      }
    } catch (error) {
      // Fallback to copying URL
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert('Review link copied to clipboard!');
      } catch (clipboardError) {}
    }
  };

  // Helper functions imported from helper-functions.ts

  // Convert rating to half-star increments (4.2 -> 4.5, 4.9 -> 5.0, etc.)
  const getRoundedRating = (rating: number): number => {
    if (rating >= 4.75) return 5.0;
    if (rating >= 4.25) return 4.5;
    if (rating >= 3.75) return 4.0;
    if (rating >= 3.25) return 3.5;
    if (rating >= 2.75) return 3.0;
    if (rating >= 2.25) return 2.5;
    if (rating >= 1.75) return 2.0;
    if (rating >= 1.25) return 1.5;
    if (rating >= 0.75) return 1.0;
    return 0.5;
  };

  // Generate star elements based on rating
  const renderStars = (rating: number) => {
    const roundedRating = getRoundedRating(rating);
    const fullStars = Math.floor(roundedRating);
    const hasHalfStar = roundedRating % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <>
        {[...Array(fullStars)].map((_, i) => (
          <img
            key={`full-${i}`}
            src="/locations/solid-star.svg"
            alt="Star"
            className="h-[22px] w-[22px]"
          />
        ))}
        {hasHalfStar && (
          <img src="/locations/half-star.svg" alt="Half Star" className="h-[22px] w-[22px]" />
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <img
            key={`empty-${i}`}
            src="/locations/solid-star.svg"
            alt="Empty Star"
            className="h-[22px] w-[22px] opacity-20"
          />
        ))}
      </>
    );
  };

  // Map WordPress locations to store values for POS integration
  const createStoreValueMap = () => {
    const storeValueMap: Record<string, string> = {};

    // Create mapping from WordPress location slugs to store values
    stores.forEach((store) => {
      // Get the slug from the URL (e.g., '/dispensary-locations/oxnard-dispensary' -> 'oxnard-dispensary')
      const urlParts = store.url.split('/').filter(Boolean);
      const slug = urlParts[urlParts.length - 1];
      if (slug) {
        storeValueMap[slug] = store.value;
        // Also add version without '-dispensary' suffix for fallback
        const shortSlug = slug.replace('-dispensary', '');
        storeValueMap[shortSlug] = store.value;
      }
    });

    // Add some specific mappings that might not follow the pattern
    storeValueMap['mid-city-los-angeles-dispensary'] = 'catalystmidcity';
    storeValueMap['normandie-los-angeles-dispensary'] = 'catalystnormandie';
    storeValueMap['oc3-santa-ana-dispensary'] = 'oc3';
    storeValueMap['san-bernadino-dispensary'] = 'catalystsbinland'; // Note the typo in the URL
    storeValueMap['retro-row-long-beach-dispensary'] = 'catalystlift';
    storeValueMap['retro-row'] = 'catalystlift'; // Additional mapping for short name
    storeValueMap['hawthorne-cannabis-lounge'] = 'catalysthawthorne'; // Same store as hawthorne dispensary (with consumption lounge)

    return storeValueMap;
  };

  // Haversine formula to calculate distance between two coordinates in miles
  const calculateHaversineDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number,
  ): number => {
    const R = 3959; // Earth's radius in miles
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance;
  };

  // Get the 3 closest stores based on actual coordinates using Haversine formula
  const getClosestStores = (
    currentLocation: typeof location,
    wordpressLocations: typeof allLocations,
  ) => {
    // Filter out coming soon locations
    const availableLocations = wordpressLocations.filter(
      (loc) => !loc.locationDetails?.comingSoon?.includes('Yes'),
    );

    // Get current location's coordinates
    let currentLat = currentLocation?.locationDetails?.addressmap?.latitude;
    let currentLon = currentLocation?.locationDetails?.addressmap?.longitude;

    // Fallback: If we're on Hawthorne Cannabis Lounge and it doesn't have coordinates,
    // use the Hawthorne Dispensary coordinates (same location)
    if ((!currentLat || !currentLon) && currentLocation?.slug === 'hawthorne-cannabis-lounge') {
      const hawthorneDispensary = wordpressLocations.find(
        (loc) => loc.slug === 'hawthorne-dispensary',
      );
      if (hawthorneDispensary?.locationDetails?.addressmap) {
        currentLat = hawthorneDispensary.locationDetails.addressmap.latitude;
        currentLon = hawthorneDispensary.locationDetails.addressmap.longitude;
      }
    }

    if (!currentLat || !currentLon) {
      // If no coordinates, filter out current location and same store locations
      return availableLocations
        .filter((loc) => {
          const isSameStore =
            (currentLocation?.slug === 'hawthorne-dispensary' &&
              loc.slug === 'hawthorne-cannabis-lounge') ||
            (currentLocation?.slug === 'hawthorne-cannabis-lounge' &&
              loc.slug === 'hawthorne-dispensary');
          return loc.slug !== currentLocation?.slug && !isSameStore;
        })
        .slice(0, 4);
    }

    // Calculate distances for all other stores using Haversine formula
    const storesWithDistance = availableLocations
      .filter((loc) => {
        // Filter out the current location and locations without coordinates
        // Also filter out Hawthorne Lounge if we're on Hawthorne Dispensary (same store)
        // And filter out Hawthorne Dispensary if we're on Hawthorne Lounge (same store)
        const isSameStore =
          (currentLocation?.slug === 'hawthorne-dispensary' &&
            loc.slug === 'hawthorne-cannabis-lounge') ||
          (currentLocation?.slug === 'hawthorne-cannabis-lounge' &&
            loc.slug === 'hawthorne-dispensary');

        return (
          loc.slug !== currentLocation?.slug &&
          !isSameStore &&
          loc.locationDetails?.addressmap?.latitude &&
          loc.locationDetails?.addressmap?.longitude
        );
      })
      .map((loc) => {
        const lat = loc.locationDetails?.addressmap?.latitude ?? 0;
        const lon = loc.locationDetails?.addressmap?.longitude ?? 0;
        const distance = calculateHaversineDistance(currentLat, currentLon, lat, lon);
        return {
          ...loc,
          distance,
        };
      })
      .sort((a, b) => a.distance - b.distance);

    // Return the 4 closest stores for desktop display
    return storesWithDistance.slice(0, 4);
  };

  // Set the active store when landing on a location page
  useEffect(() => {
    // activeStore already carries a valid ?selectedStore= override (see the loader)
    const storeToUse = activeStore;

    // Check if user just selected a store via store selector
    const userSelectedStore = localStorage.getItem('userSelectedStore');

    // Only update the store if:
    // 1. We have a store from the URL
    // 2. It's different from current selection
    // 3. No store is currently selected (initial load) OR user didn't just select a store
    if (storeToUse && storeToUse !== selectedStore && (!selectedStore || !userSelectedStore)) {
      // Clear the user selection flag since we're processing it
      if (userSelectedStore) {
        localStorage.removeItem('userSelectedStore');
      }

      // Wrap store updates in startTransition to prevent hydration conflicts
      startTransition(() => {
        // Set the store when landing on a location page
        setSelectedStore(storeToUse);
        localStorage.setItem('selectedStore', storeToUse);
        document.cookie = `selectedStore=${storeToUse}; path=/; max-age=${60 * 60 * 24 * 365}`;
      });

      // Also ensure we have a token for this store
      (async () => {
        const tokenKey = `token_${storeToUse}`;
        const existingToken = localStorage.getItem(tokenKey);
        if (!existingToken) {
          const token = await generateTokenForStore(storeToUse);
          if (token) {
            localStorage.setItem(tokenKey, token);
            document.cookie = `${tokenKey}=${encodeURIComponent(token)}; path=/; max-age=${60 * 60 * 24 * 365}`;
          }
        }
        // Wrap the event dispatch in startTransition as well
        startTransition(() => {
          window.dispatchEvent(new Event('store:change'));
        });
      })();
    } else if (userSelectedStore) {
      // If user selected a store but we're not updating, still clear the flag
      localStorage.removeItem('userSelectedStore');
    }
  }, [activeStore, selectedStore, setSelectedStore]);

  // Calculate location-specific store value for deals and hour overrides
  const locationSpecificStore = (() => {
    if (!location?.slug) return activeStore || selectedStore;

    const storeValueMap = createStoreValueMap();
    return storeValueMap[location.slug] || activeStore || selectedStore;
  })();

  // Extract location data using helper function
  // Pass storeValue to apply date-based hour overrides (e.g., Thanksgiving, Black Friday)
  const {
    locationName,
    locationAddress,
    locationHours,
    locationPhone,
    locationEmail,
    directionsUrl,
    seoTitle,
    mapEmbed,
    seoText,
    comingSoon,
    imageAlt,
  } = extractLocationData(location, locationSpecificStore);

  const isComingSoon = Array.isArray(comingSoon) && comingSoon.includes('Yes');
  const shopNowLabel = isStoreInactive ? 'Temporarily Closed' : isComingSoon ? 'Coming Soon' : 'Shop Now';
  const isShopNowDisabled = isComingSoon || isStoreInactive;

  // Use fallback data with imported defaults
  const faqs = location?.locationDetails?.faqs || getDefaultFaqData(locationName);

  // Prepare location images - ready for multiple images when backend implements storeGallery
  const locationImages = (() => {
    const images: string[] = [];

    // Add main store image if available
    if (location?.locationDetails?.storeImage?.node?.mediaItemUrl) {
      images.push(location.locationDetails.storeImage.node.mediaItemUrl);
    }

    // Add gallery images from WordPress storeGallery field
    const storeGallery = location?.locationDetails?.storeGallery;
    if (storeGallery?.nodes) {
      const galleryUrls = storeGallery.nodes
        .map((node) => node.mediaItemUrl)
        .filter((url: string) => url && !images.includes(url));
      images.push(...galleryUrls);
    }

    return images;
  })();

  // Get gallery images using helper function
  const galleryImages = getGalleryImages(locationImages);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Handle shop now button click - sets store and navigates to shop
  const handleShopNow = async () => {
    try {
      // Set the store using the existing store context
      const storeValue = activeStore || selectedStore;

      // Generate token if needed
      const tokenKey = `token_${storeValue}`;
      const existingToken = localStorage.getItem(tokenKey);
      if (!existingToken) {
        const token = await generateTokenForStore(storeValue);
        if (token) {
          localStorage.setItem(tokenKey, token);
          document.cookie = `${tokenKey}=${encodeURIComponent(token)}; path=/; max-age=${60 * 60 * 24 * 365}`;
        }
      }

      // Update store selection
      setSelectedStore(storeValue);
      localStorage.setItem('selectedStore', storeValue);
      document.cookie = `selectedStore=${storeValue}; path=/; max-age=${60 * 60 * 24 * 365}`;

      // Dispatch store change event
      window.dispatchEvent(new Event('store:change'));

      // Navigate to shop page
      window.location.href = '/shop';
    } catch (error) {
      console.error('Error in handleShopNow:', error);
    }
  };

  // Handle shop now for nearby dispensary cards - sets specific store and navigates to shop
  const handleShopNowForStore = async (storeValue: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      // Generate token if needed
      const tokenKey = `token_${storeValue}`;
      const existingToken = localStorage.getItem(tokenKey);
      if (!existingToken) {
        const token = await generateTokenForStore(storeValue);
        if (token) {
          localStorage.setItem(tokenKey, token);
          document.cookie = `${tokenKey}=${encodeURIComponent(token)}; path=/; max-age=${60 * 60 * 24 * 365}`;
        }
      }

      // Update store selection
      setSelectedStore(storeValue);
      localStorage.setItem('selectedStore', storeValue);
      document.cookie = `selectedStore=${storeValue}; path=/; max-age=${60 * 60 * 24 * 365}`;

      // Dispatch store change event
      window.dispatchEvent(new Event('store:change'));

      // Navigate to shop page
      window.location.href = '/shop';
    } catch (error) {
      console.error('Error in handleShopNowForStore:', error);
    }
  };

  // Define each section as a variable - following the collection page pattern
  // Compute breadcrumb items using loader data (ensures server/client consistency)
  const processedLocationName = locationName.replace(' Dispensary', '').replace(/^Catalyst /, '');
  const breadcrumbItems = [
    { name: 'Home', url: '/' },
    { name: 'Locations', url: '/dispensary-locations' },
    { name: processedLocationName, url: `/dispensary-locations/${params.location}` },
  ];

  const breadcrumbSection = (
    <div className="w-full px-4">
      <div className="mx-auto max-w-[90rem]">
        <Breadcrumbs items={breadcrumbItems} />
      </div>
    </div>
  );

  const imageGallerySection = (
    <div className="flex flex-col items-start gap-6 bg-[#F5F5F5] px-4">
      {/* Main Image Display */}
      <div
        className="w-full overflow-hidden rounded-lg bg-[#F5F5F5] shadow-[0px_4px_8px_rgba(0,0,0,0.15)]"
        style={{ aspectRatio: '4/2.5' }}
      >
        <img
          src={galleryImages[selectedImageIndex]}
          alt={
            selectedImageIndex === 0
              ? imageAlt || `${locationName} - Main view`
              : `${locationName} - Main view`
          }
          className="h-full w-full object-cover transition-opacity duration-300"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src =
              '/images/catalyst-antioch-cannabis-dispensary-interior-display-cases-5851-1-scaled.webp';
          }}
        />
      </div>

      {/* Thumbnail Navigation - Scrollable Carousel */}
      <div
        className="scrollbar-hide-mobile w-full overflow-x-auto pb-2"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div className="flex gap-[10px]" style={{ minWidth: 'min-content' }}>
          {galleryImages.map((image, index) => (
            <button
              key={index}
              onClick={() => setSelectedImageIndex(index)}
              className={`aspect-[88/54] w-[calc(30%-8px)] min-w-[calc(30%-8px)] overflow-hidden rounded-[2px] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 md:w-[calc(25%-8px)] md:min-w-[calc(25%-8px)] lg:aspect-[142/87] ${
                index === selectedImageIndex
                  ? 'border-2 border-[#264B9A] shadow-[0px_1px_12px_rgba(38,75,154,0.5)]'
                  : ''
              }`}
              aria-label={`View image ${index + 1} of ${galleryImages.length}`}
            >
              <img
                src={image}
                alt={
                  index === 0
                    ? imageAlt || `${locationName} - View ${index + 1}`
                    : `${locationName} - View ${index + 1}`
                }
                className="h-full w-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src =
                    '/images/catalyst-antioch-cannabis-dispensary-interior-display-cases-5851-1-scaled.webp';
                }}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const aboutDispensarySection = (
    <section
      id="about-dispensary"
      className="space-y-4 bg-white px-4 py-4"
    >
      {/* Section Header */}
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          <img src="/locations/tick.svg" alt="Facility information" className="h-6 w-6" />
        </div>
        <h3 className="text-[32px] text-gray-900">About this Facility</h3>
      </div>

      {/* Content */}
      <div className="prose prose-gray ml-[36px] max-w-none">
        <div
          className="cms-rich-text font-helvetica-neue leading-relaxed text-gray-700 [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800 [&_h1]:mt-6 [&_h1]:mb-3 [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:mt-6 [&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-bold [&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h4]:mt-4 [&_h4]:mb-2 [&_h4]:font-semibold [&_h5]:mt-4 [&_h5]:mb-2 [&_h5]:font-semibold [&_h6]:mt-4 [&_h6]:mb-2 [&_h6]:font-semibold [&_li]:mb-1 [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-4 [&_p]:leading-6 [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-6"
          dangerouslySetInnerHTML={{ __html: seoText }}
        />
      </div>
    </section>
  );

  const directionsLocationSection = (
    <section
      id="directions-location"
      className="flex w-full flex-col items-center gap-4 bg-white px-4 py-4"
    >
      {/* Section Header */}
      <div className="flex w-full items-center gap-3 bg-white">
        <div className="flex-shrink-0">
          <img src="/locations/Marker.svg" alt="Location marker" className="h-6 w-6" />
        </div>
        <h2 className="text-[32px] text-gray-900">
          Directions & Location — {processedLocationName}
        </h2>
      </div>

      {/* Map Container */}
      <div className="map-container w-full overflow-hidden lg:ps-10">
        {mapEmbed ? (
          <div
            dangerouslySetInnerHTML={{ __html: mapEmbed }}
            className="directions-map-embed h-[412px] w-full overflow-hidden [&_iframe]:h-[412px] [&_iframe]:w-full [&_iframe]:rounded-lg [&_iframe]:border [&_iframe]:border-slate-300"
          />
        ) : (
          <div className="flex h-[412px] w-full items-center justify-center border border-[#CBD5E1] bg-gray-50">
            <div className="text-center">
              <img
                src="/locations/Marker.svg"
                alt="Location"
                className="mx-auto mb-4 h-16 w-16 text-gray-400"
              />
              <p className="text-gray-600">
                Map will be displayed here
                <br />
                <span className="text-sm">{locationAddress}</span>
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );


  /**
   * ONE hero, rendered once, reflowed by CSS.
   *
   * There used to be two: `mobileHeaderSection` + `mobileStoreDescriptionSection`
   * behind `lg:hidden`, and an inline desktop block behind `hidden lg:block`.
   * Both carried the same store name, rating, address, hours, description and
   * CTAs, so all of it sat in the DOM twice — including the intro copy the SEO
   * team reported, and the image gallery.
   *
   * Two things that duplication was hiding:
   *   - The store name was an <h1> on mobile and an <h2> on desktop, so the page
   *     shipped both, with identical text. It is one <h1> now.
   *   - The two descriptions did NOT agree. Desktop skipped `location.content`
   *     when it contained `uabb-faq` (legacy WordPress FAQ markup) and fell back
   *     to the CMS intro; mobile rendered it raw. The desktop rule is the safe
   *     one and is what survives here.
   *
   * Layout differences are expressed with `order-*` and responsive columns:
   * mobile leads with the details then the gallery, desktop puts the gallery
   * left and the details right.
   */
  const heroSection = (
    <section className="w-full bg-[#F5F5F5] px-4 pb-8 lg:px-4 lg:py-8">
      <div className="location-hero mx-auto max-w-[106rem]">
        <div className="location-hero__title flex flex-col items-start gap-2">
          <h1 className="font-poppins text-[32px] leading-10 font-bold text-[#1E293B] lg:text-[3rem]">
            {locationName.replace(/^Catalyst /, '')}
          </h1>

          {location?.locationDetails?.rating && location?.locationDetails?.reviewCount && (
            <div className="flex items-center gap-3">
              <span className="text-lg leading-[18px] text-[#1E293B]">
                {location.locationDetails.rating.toFixed(1)}
              </span>
              <div className="flex items-center gap-0">
                {renderStars(location.locationDetails.rating)}
              </div>
              <span className="text-sm leading-[18px] font-normal text-[#374151]">
                ({location.locationDetails.reviewCount} Reviews)
              </span>
            </div>
          )}
        </div>

        <div className="location-hero__meta flex flex-col items-start gap-4 py-0.5">
          <div className="flex items-center gap-4 pl-px">
            <img src="/locations/Map.svg" alt="Map directions" className="h-6 w-[18px]" />
            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-helvetica-neue text-lg leading-[22px] font-normal text-[#4A5565] underline hover:text-[#264B9A]"
            >
              {locationAddress}
            </a>
          </div>

          <div className="flex items-center gap-4 pl-px">
            <img src="/locations/Clock.svg" alt="Store hours" className="h-[18px] w-[18px]" />
            <span className="font-helvetica-neue text-lg leading-[22px] font-normal text-[#4A5565]">
              {locationHours.replace('DAILY - ', '').replace('Open ', '')}
            </span>
          </div>
        </div>

        <div className="location-hero__gallery">{imageGallerySection}</div>

        <div className="location-hero__desc prose prose-gray max-w-none">
          {/* Separator sat above the description in both old layouts. */}
          <div className="mb-4 h-0.5 w-full bg-[#CBD5E1]"></div>
          <div
            className="cms-rich-text font-helvetica-neue text-base leading-[150%] font-normal text-[#181818] lg:text-lg"
            dangerouslySetInnerHTML={{
              __html:
                location?.content && !location.content.includes('uabb-faq')
                  ? location.content
                  : location?.locationDetails?.storeDetailPageIntro ||
                    `Catalyst Dispensary is the best place to buy cannabis in ${locationName.replace(/^Catalyst /, '')} for a reason. We have Fire weed at Fire prices! Our budtenders are industry experts — the best in the business — and can help everyone from the occasional smoker to the everyday consumer find the perfect product.`,
            }}
          />
        </div>

        <div className="location-hero__cta flex w-full items-center gap-4">
          <button
            onClick={handleShopNow}
            disabled={isShopNowDisabled}
            aria-disabled={isShopNowDisabled}
            title={shopNowLabel}
            className={`font-helvetica-neue flex flex-1 items-center justify-center gap-3 rounded-lg py-3 text-sm leading-6 font-semibold uppercase md:px-6 lg:w-fit lg:flex-none lg:px-6 ${
              isShopNowDisabled
                ? 'cursor-not-allowed bg-gray-300 text-gray-600'
                : 'bg-[#264B9A] text-white hover:bg-blue-700'
            }`}
          >
            {shopNowLabel.toUpperCase()}
          </button>
          <a
            href={`tel:${locationPhone.replace(/[^+\d]/g, '')}`}
            className="flex flex-1 items-center justify-center gap-3 rounded-lg border-2 border-[#264B9A] py-3 text-sm leading-6 text-[#264B9A] transition-colors hover:bg-blue-50 md:px-6 lg:w-fit lg:flex-none lg:px-6"
          >
            <img src="/locations/phone.svg" alt="Phone contact" className="h-4 w-4" />
            <span>{locationPhone}</span>
          </a>
        </div>
      </div>
    </section>
  );

  const localDealsSection = (
    <div id="local-deals" className="w-full">
      {/* Deals CTA */}
      <div className="rounded-lg bg-white p-4">
        <h2 className="font-futura mb-3 flex items-center gap-2 text-xl font-bold text-gray-900">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6 text-[#264B9A]" aria-hidden="true">
            <path fillRule="evenodd" d="M5.25 2.25a3 3 0 00-3 3v4.318a3 3 0 00.879 2.121l9.58 9.581c.92.92 2.39.92 3.31 0l4.318-4.318a2.25 2.25 0 000-3.31l-9.58-9.581A3 3 0 008.568 2.25H5.25zM6.75 6a.75.75 0 100 1.5.75.75 0 000-1.5z" clipRule="evenodd" />
          </svg>
          Local Deals
        </h2>
        <p className="mb-4 text-sm text-gray-600">
          Check out the latest deals and discounts available at Catalyst {processedLocationName}.
        </p>
        <Link
          to="/deals"
          className="font-futura block w-full rounded-lg bg-[#264B9A] px-6 py-3 text-center text-sm font-bold text-white uppercase transition-colors hover:bg-[#1a3670]"
        >
          View Deals at {processedLocationName}
        </Link>
      </div>

      {/* Deals Content Text - appears after CTA */}
      {location?.locationDetails?.dealsContent && (
        <div className="bg-white px-4 pt-4">
          <div className="prose prose-gray max-w-none">
            <div
              className="cms-rich-text font-helvetica-neue leading-relaxed text-gray-700 [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800 [&_h1]:mt-6 [&_h1]:mb-3 [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:mt-6 [&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-bold [&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h4]:mt-4 [&_h4]:mb-2 [&_h4]:font-semibold [&_h5]:mt-4 [&_h5]:mb-2 [&_h5]:font-semibold [&_h6]:mt-4 [&_h6]:mb-2 [&_h6]:font-semibold [&_li]:mb-1 [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-4 [&_p]:leading-6 [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-6"
              dangerouslySetInnerHTML={{ __html: location.locationDetails.dealsContent }}
            />
          </div>
        </div>
      )}
    </div>
  );

  // Extract the Elfsight widget div class from the reviewsWidget HTML (strip the script tag)
  const elfsightWidgetClass = location?.locationDetails?.reviewsWidget
    ? location.locationDetails.reviewsWidget.match(/class="(elfsight-app-[^"]+)"/)?.[1]
    : null;

  // Load Elfsight platform.js once via useEffect so the script actually executes
  useEffect(() => {
    if (!elfsightWidgetClass) return;
    // Don't add the script if it's already on the page
    if (document.querySelector('script[src*="static.elfsight.com/platform/platform.js"]')) {
      // Script already loaded — re-init if the widget API is available
      if ((window as any).eapps) {
        try { (window as any).eapps.Platform.init(); } catch (_) {}
      }
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://static.elfsight.com/platform/platform.js';
    script.async = true;
    document.body.appendChild(script);
  }, [elfsightWidgetClass]);

  const customerReviewsSection = elfsightWidgetClass ? (
    <section id="customer-reviews" className="space-y-4 bg-white px-4 py-4">
      {/* Section Header */}
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          <img src="/locations/cannabis-Star.svg" alt="Reviews section" className="h-6 w-6" />
        </div>
        <h2 className="text-[32px] text-gray-900">Customer Reviews</h2>
      </div>

      {/* Elfsight Google Reviews Widget — rendered as a div, script loaded via useEffect */}
      <div className="reviews-widget-container overflow-hidden">
        <div className={elfsightWidgetClass} data-elfsight-app-lazy />
      </div>
    </section>
  ) : null;

  const postReviewsContentSection = location?.locationDetails?.postReviewsContent ? (
    <section
      id="post-reviews-content"
      className="space-y-4 bg-white px-4 py-4"
    >
      <div className="prose prose-gray max-w-none">
        <div
          className="cms-rich-text font-helvetica-neue leading-relaxed text-gray-700 [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800 [&_h1]:mt-6 [&_h1]:mb-3 [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:mt-6 [&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-bold [&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h4]:mt-4 [&_h4]:mb-2 [&_h4]:font-semibold [&_h5]:mt-4 [&_h5]:mb-2 [&_h5]:font-semibold [&_h6]:mt-4 [&_h6]:mb-2 [&_h6]:font-semibold [&_li]:mb-1 [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-4 [&_p]:leading-6 [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-6"
          dangerouslySetInnerHTML={{ __html: location.locationDetails.postReviewsContent }}
        />
      </div>
    </section>
  ) : null;

  const rewardsBannerSection = (
    <div className="flex w-full flex-col gap-4">
      {/* Deals CTA */}
      <Link
        to="/deals"
        className="flex items-center justify-center gap-2 rounded-lg bg-[#264B9A] px-6 py-3.5 text-center font-bold text-white uppercase transition-colors hover:bg-[#1a3670]"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
          <path fillRule="evenodd" d="M5.25 2.25a3 3 0 00-3 3v4.318a3 3 0 00.879 2.121l9.58 9.581c.92.92 2.39.92 3.31 0l4.318-4.318a2.25 2.25 0 000-3.31l-9.58-9.581A3 3 0 008.568 2.25H5.25zM6.75 6a.75.75 0 100 1.5.75.75 0 000-1.5z" clipRule="evenodd" />
        </svg>
        View Deals
      </Link>

      {/* Rewards Button */}
      <a
        href="/register"
        className="w-full rounded-lg bg-green-600 px-6 py-3 text-center font-semibold text-white transition-colors hover:bg-green-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
      >
        Earn Rewards - Join Now
      </a>
    </div>
  );

  const nearbyDispensariesSection = (
    <section
      id="nearby-dispensaries"
      className="flex flex-col items-center gap-10 bg-[#F8FAFC] px-4 py-10"
    >
      {/* Header Copy */}
      <div className="flex w-full max-w-[106rem] flex-col items-center gap-0">
        <div className="flex w-full flex-col items-start gap-0">
          <h2 className="font-poppins w-full text-2xl leading-8 font-medium text-[#181818]">
            Shop Nearby Dispensaries
          </h2>
        </div>
      </div>

      {/* Cards - Responsive Grid: 1 mobile, 2-3 tablet, 4 desktop */}
      <div className="grid w-full max-w-[106rem] grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {getClosestStores(location, allLocations).map((wpLocation, index) => {
          // Get store value for this WordPress location
          const storeValueMap = createStoreValueMap();
          const storeValue = storeValueMap[wpLocation.slug];
          const storeData = stores.find((s) => s.value === storeValue);

          // Check for date-based hour override (e.g., Thanksgiving, Black Friday)
          const override = storeValue ? getStoreHoursOverride(storeValue) : null;
          const displayHours = override
            ? override.hours
            : wpLocation.locationDetails.openingHours || 'DAILY - 8AM to 8PM';

          // Use WordPress image if available, otherwise fallback to default
          const imageSrc =
            wpLocation.locationDetails.storeImage?.node?.mediaItemUrl || getDispensaryImage(index);
          const imageAltText =
            wpLocation.locationDetails.storeImage?.node?.altText ||
            `${wpLocation.title} dispensary`;

          return (
            <div
              key={wpLocation.slug}
              onClick={() => navigate(`/dispensary-locations/${wpLocation.slug}`)}
              className="flex min-h-[500px] w-full cursor-pointer flex-col items-start gap-0 rounded-xl bg-white shadow-[0px_4px_8px_rgba(0,0,0,0.15)] transition-transform hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#264B9A] focus-visible:ring-offset-2"
              role="link"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/dispensary-locations/${wpLocation.slug}`);
                }
              }}
              aria-label={`View ${wpLocation.title} dispensary location`}
            >
              {/* Image Container */}
              <div className="relative h-[200px] w-full rounded-t-xl bg-[#006AA8]">
                <img
                  src={imageSrc}
                  alt={imageAltText}
                  className="h-[200px] w-full rounded-t-xl object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      '/images/catalyst-antioch-cannabis-dispensary-interior-display-cases-5851-1-scaled.webp';
                  }}
                />
              </div>

              {/* Content */}
              <div className="flex w-full flex-1 flex-col items-center gap-3 px-4 py-6">
                {/* Header */}
                <div className="flex w-full flex-col items-center gap-2">
                  {/* Location & Stars */}
                  <div className="flex w-full items-center gap-2.5">
                    <div className="flex flex-1 items-center gap-1">
                      <span className="font-helvetica-neue text-xs leading-[18px] font-normal text-gray-600">
                        (5.0)
                      </span>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <img
                            key={i}
                            src="/locations/cannabis-gold.svg"
                            alt="Star"
                            className="h-[18px] w-[17.87px]"
                          />
                        ))}
                      </div>
                    </div>
                    {/* Distance */}
                    {(wpLocation as any).distance && (
                      <span className="font-helvetica-neue text-xs leading-[18px] font-normal text-[#4A5565]">
                        {(wpLocation as any).distance.toFixed(1)} miles away
                      </span>
                    )}
                  </div>

                  {/* H4 */}
                  <div className="flex w-full items-start gap-2.5">
                    <h3 className="font-helvetica-neue text-start text-2xl leading-tight font-medium text-[#0F172A]">
                      {wpLocation.title} Dispensary
                    </h3>
                  </div>
                </div>

                {/* Location & Hours */}
                <div className="flex w-full flex-col items-start gap-3 pb-4">
                  {/* Address */}
                  <div className="flex w-full items-start gap-3 pl-0.5">
                    <img src="/locations/Map.svg" alt="Store location" className="h-6 w-[18px]" />
                    <span className="font-helvetica-neue flex-1 text-lg leading-[22px] font-normal text-[#4A5565]">
                      {wpLocation.locationDetails.address}
                    </span>
                  </div>

                  {/* Hours */}
                  <div className="flex w-full items-center gap-3 pl-0.5">
                    <img
                      src="/locations/Clock.svg"
                      alt="Store hours"
                      className="h-[18px] w-[18px]"
                    />
                    <span className="font-helvetica-neue text-lg leading-[22px] font-normal text-[#4A5565]">
                      {displayHours.replace('DAILY - ', '').replace('Open ', '')}
                    </span>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex w-full items-start justify-center gap-4">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/dispensary-locations/${wpLocation.slug}`);
                    }}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-[#264B9A] bg-[#264B9A] px-2 py-3 transition-colors hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#264B9A] focus-visible:ring-offset-2"
                  >
                    <span className="font-helvetica-neue text-center text-base leading-4 font-medium text-nowrap text-white">
                      Change Store
                    </span>
                  </button>
                  <a
                    href={`tel:${wpLocation.locationDetails.phone || '(925) 276-9420'}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-[#264B9A] px-6 py-3 transition-colors hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#264B9A] focus-visible:ring-offset-2"
                  >
                    <img src="/locations/phone.svg" alt="Phone contact" className="h-4 w-4" />
                    <span className="font-helvetica-neue text-center text-base leading-4 font-medium text-nowrap text-[#264B9A]">
                      {wpLocation.locationDetails.phone || '(925) 276-9420'}
                    </span>
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );

  // Scroll to section function
  const scrollToSection = (sectionName: string) => {
    const sections = {
      'About this dispensary': 'about-dispensary',
      'Directions & locations': 'directions-location',
      'Local deals': 'local-deals',
      'Customer reviews': 'customer-reviews',
      'Frequently asked questions': 'faqs',
      'Nearby dispensaries': 'nearby-dispensaries',
    };

    const elementId = sections[sectionName as keyof typeof sections];
    if (elementId) {
      // One element per section now. The old `${id}-desktop` lookup with a
      // fallback existed only because the page rendered both layouts and could
      // not give two copies the same id — the duplication was working around
      // itself. With a single DOM the plain id is unambiguous.
      const element = document.getElementById(elementId);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const desktopSidebarSection = (
    <div className="flex flex-col gap-6">
      {/* Table of Contents */}
      <div className="flex w-full flex-col items-start gap-3 rounded-lg bg-white px-6 shadow-[0px_0px_20px_4px_rgba(0,0,0,0.05)]">
        <div className="flex w-full flex-col items-start gap-0">
          {[
            {
              name: 'About this dispensary',
              icon: '/locations/tick.svg',
              altText: 'Dispensary info',
            },
            {
              name: 'Directions & locations',
              icon: '/locations/Marker.svg',
              altText: 'Location pin',
            },
            { name: 'Local deals', icon: '/locations/deals-tag.svg', altText: 'Deals tag' },
            {
              name: 'Customer reviews',
              icon: '/locations/cannabis-Star.svg',
              altText: 'Review star',
            },
            {
              name: 'Frequently asked questions',
              icon: '/locations/FAQs.svg',
              altText: 'FAQ help',
            },
            {
              name: 'Nearby dispensaries',
              icon: '/locations/building-icon.svg',
              altText: 'Building icon',
            },
          ].map((item, index) => (
            <div key={index} className="w-full">
              {/* List Item */}
              <button
                onClick={() => scrollToSection(item.name)}
                className="flex w-full items-center gap-4 rounded px-3 py-5 transition-colors hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
              >
                {/* Icon */}
                <div className="flex h-4 w-4 items-center justify-center">
                  <img
                    src={item.icon}
                    alt={item.altText}
                    className="h-4 w-4"
                    style={{ filter: 'invert(0.3) sepia(1) saturate(0) hue-rotate(180deg)' }}
                  />
                </div>

                {/* Text */}
                <span className="font-['Helvetica_Neue'] text-base font-normal text-[#4A5565]">
                  {item.name}
                </span>
              </button>

              {/* Horizontal Separator */}
              {index < 5 && <div className="h-px w-full bg-[#CBD5E1]"></div>}
            </div>
          ))}
        </div>
      </div>

      {/* Rewards Banner - Desktop Sidebar */}
      <div className="w-full">{rewardsBannerSection}</div>
    </div>
  );

  // Convert time strings like "9AM", "9:30PM", "9:00am" to ISO 24h format "09:00", "21:30"
  const parseTimeToISO = (timeStr: string): string => {
    if (!timeStr) return '09:00';
    const cleaned = timeStr.replace(/\s+/g, '').toUpperCase();
    const match = cleaned.match(/(\d{1,2})(?::(\d{2}))?(?:\s*)?(AM|PM)?/);
    if (!match) return '09:00';
    let hours = parseInt(match[1], 10);
    const minutes = match[2] || '00';
    const period = match[3];
    if (period === 'PM' && hours < 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  };

  // Map day abbreviations to schema.org day names
  const dayMap: Record<string, string[]> = {
    'sun': ['Sunday'], 'mon': ['Monday'], 'tue': ['Tuesday'], 'wed': ['Wednesday'],
    'thu': ['Thursday'], 'thurs': ['Thursday'], 'fri': ['Friday'], 'sat': ['Saturday'],
    'daily': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  };

  // Expand day ranges like "Sun-Thurs" to individual days
  const expandDayRange = (rangeStr: string): string[] => {
    const allDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const cleaned = rangeStr.trim().toLowerCase().replace(/[:\s]+$/, '');

    // Check for single day or "daily"
    if (dayMap[cleaned]) return dayMap[cleaned];

    // Handle range like "Sun-Thurs" or "Fri-Sat"
    const rangeParts = cleaned.split(/\s*-\s*/);
    if (rangeParts.length === 2) {
      const startDay = Object.entries(dayMap).find(([k]) => rangeParts[0].startsWith(k))?.[1]?.[0];
      const endDay = Object.entries(dayMap).find(([k]) => rangeParts[1].startsWith(k))?.[1]?.[0];
      if (startDay && endDay) {
        const startIdx = allDays.indexOf(startDay);
        const endIdx = allDays.indexOf(endDay);
        if (startIdx >= 0 && endIdx >= 0) {
          const days: string[] = [];
          let i = startIdx;
          while (true) {
            days.push(allDays[i]);
            if (i === endIdx) break;
            i = (i + 1) % 7;
          }
          return days;
        }
      }
    }
    return allDays; // fallback to all days
  };

  // Parse opening hours - handles "Sun-Thurs: 9:00am-9:00pm, Fri-Sat: 9:00am-10:00pm" and "DAILY - 9AM-9:30PM"
  const parseOpeningHours = (hoursStr: string): Array<{ dayOfWeek: string[]; opens: string; closes: string }> | null => {
    if (!hoursStr) return null;

    // Check for multi-schedule format (contains day abbreviations with colons)
    const multiSchedule = hoursStr.split(/,\s*/).filter(s => s.trim());
    if (multiSchedule.length > 1 || hoursStr.match(/(?:sun|mon|tue|wed|thu|fri|sat)/i)) {
      const specs: Array<{ dayOfWeek: string[]; opens: string; closes: string }> = [];
      for (const segment of multiSchedule) {
        // Match "Sun-Thurs: 9:00am-9:00pm" or "Fri-Sat: 9:00am-10:00pm"
        const match = segment.match(/^([^:]+?):\s*(.+)$/i);
        if (match) {
          const days = expandDayRange(match[1]);
          const times = match[2].trim().split(/\s*-\s*/);
          if (times.length >= 2) {
            specs.push({
              dayOfWeek: days,
              opens: parseTimeToISO(times[0]),
              closes: parseTimeToISO(times[times.length - 1]),
            });
          }
        }
      }
      if (specs.length > 0) return specs;
    }

    // Simple format: "DAILY - 9AM-9:30PM" or "Open 9:00 AM – 6:00 PM"
    const cleaned = hoursStr.replace(/^(DAILY\s*-\s*|Open\s*)/i, '').trim();
    const parts = cleaned.split(/\s*[–—]\s*|\s+to\s+|\s*-\s*(?=\d)/);
    if (parts.length < 2) return null;
    return [{
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: parseTimeToISO(parts[0]),
      closes: parseTimeToISO(parts[parts.length - 1]),
    }];
  };

  const parsedHours = parseOpeningHours(locationHours);

  // Store Central holds the address already split, the coordinates, and the
  // maps link. Prefer all three: `address` is a display string that has to be
  // taken apart to build a PostalAddress, and splitting it on commas is wrong
  // the moment a street line contains one of its own — Bellflower is
  // "9032 Artesia Blvd, Bldg B, Bellflower, CA 90706". The Store Central
  // coordinates also supersede website_locations, which had drifted ~240m on
  // 31 of the 34 stores.
  //
  // Resolved in the loader, so it is server-rendered into the served HTML and
  // costs no extra hook — this component sits after an early return for the
  // near-me variant, where another conditional hook would compound an existing
  // rules-of-hooks problem.
  const addressParts = storeSchemaData?.addressParts;

  // Fallback for stores still on the hardcoded rows: read from the END, so an
  // extra building line lands in the street rather than in the city.
  const addressSegments: string[] = String(locationAddress ?? '')
    .split(',')
    .map((part: string) => part.trim())
    .filter(Boolean);
  const fallbackCity = addressSegments.length >= 2 ? addressSegments[addressSegments.length - 2] : '';
  const fallbackStreet =
    addressSegments.length >= 2 ? addressSegments.slice(0, -2).join(', ') : locationAddress;

  // Only publish a rating we actually hold. The previous `|| 4.9` / `|| 493`
  // meant any location missing review data shipped an invented AggregateRating
  // that appears nowhere on the page — that is a structured-data guidelines
  // violation, not a cosmetic default. No data, no property.
  const ratingValue = location?.locationDetails?.rating;
  const reviewCount = location?.locationDetails?.reviewCount;

  // Build FAQ items for JSON-LD (computed once, used once)
  const faqItemsForSchema = (location?.locationDetails?.faqs && location.locationDetails.faqs.length > 0)
    ? location.locationDetails.faqs
    : getDefaultFaqData(locationName);

  return (
    <>
      {/* Structured Data JSON-LD — rendered once outside mobile/desktop layouts.
          Built by the shared generateLocationSchema so this page and the
          near-[city] pages cannot drift apart again: they were emitting a rich
          Store and a four-field LocalBusiness respectively, from two
          hand-rolled objects. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            generateLocationSchema({
              name: locationName,
              url: `https://catalyst-cannabis.com/dispensary-locations/${location?.slug}`,
              description: location?.locationDetails?.seoText
                ? location.locationDetails.seoText
                    .replace(/<!--[\s\S]*?-->/g, '')
                    .replace(/<[^>]*>/g, '')
                    .trim()
                    .substring(0, 300)
                : undefined,
              address: {
                streetAddress: addressParts?.street || fallbackStreet,
                addressLocality: addressParts?.city || fallbackCity,
                addressRegion: addressParts?.state || 'CA',
                postalCode: addressParts?.zip || locationAddress.match(/\d{5}/)?.[0] || '',
              },
              phone: locationPhone,
              email: location?.locationDetails?.email,
              image:
                location?.locationDetails?.storeImage?.node?.mediaItemUrl ||
                'https://catalyst-cannabis.com/images/Catalyst-Cannabis-Dispensary-Store-Hours.webp',
              geo: storeSchemaData?.geo
                ? { latitude: storeSchemaData.geo.lat, longitude: storeSchemaData.geo.lng }
                : location?.locationDetails?.addressmap
                  ? {
                      latitude: location.locationDetails.addressmap.latitude,
                      longitude: location.locationDetails.addressmap.longitude,
                    }
                  : undefined,
              openingHoursSpecification: parsedHours ?? undefined,
              hasMap: storeSchemaData?.mapsUrl || location?.locationDetails?.directionsUrl,
              aggregateRating:
                ratingValue && reviewCount ? { ratingValue, reviewCount } : undefined,
              priceRange: '$$',
              paymentAccepted: ['Cash', 'Debit Card'],
              currenciesAccepted: 'USD',
            }),
          ),
        }}
      />

      {faqItemsForSchema && faqItemsForSchema.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqItemsForSchema.map((faq: { question: string; answer: string }) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.answer.replace(/<[^>]*>/g, ''),
                },
              })),
            }),
          }}
        />
      )}

      {/* ── ONE DOM, CSS DOES THE RESPONSIVE WORK ──────────────────────────
          This page used to render TWO COMPLETE LAYOUTS — a mobile one behind
          `lg:hidden` and a desktop one behind `hidden lg:block`. Tailwind's
          `hidden` is `display: none`, which hides an element from a VIEWER but
          leaves it fully in the DOM. So every visitor downloaded the page twice
          and, far worse, every crawler read it twice.

          Measured on the live Antioch page before this change: each FAQ
          question appeared twice in the visible DOM, as did the intro copy, the
          reviews copy and the product-category copy. 37 ids were duplicated,
          including every `faq-question-N`/`faq-answer-N` pair — invalid HTML,
          and it breaks the accordion's `aria-controls`, which then resolves to
          two elements. The SEO team reported it as "things doubling up"; they
          were right, and it was never only the FAQs.

          Now each section renders ONCE. The ids live inside the section
          variables, so rendering once makes them unique again and the
          `-desktop` wrapper ids are gone. Only the HEADER differs structurally
          between breakpoints — a stacked mobile header versus a two-column
          gallery-plus-details — so that is the one thing still branched, and it
          is chrome rather than indexable body copy. The ordering difference is
          expressed with `order-*` rather than with a second copy. */}

      {breadcrumbSection}

      {/* Hero — rendered ONCE. See heroSection for what this replaced. */}
      {heroSection}

      {/* Body — rendered once, reflowed by CSS */}
      <div className="w-full lg:mx-auto lg:max-w-[106rem] lg:p-8">
        <div
          ref={gridContainerRef}
          className="relative lg:grid lg:grid-cols-[1fr_350px] lg:gap-8"
        >
          <div className="flex flex-col lg:gap-4">
            {/* Mobile leads with directions, desktop with the facility blurb.
                That was the only ordering difference between the two old
                layouts, and `order-*` expresses it without a second copy. */}
            <div className="order-1 lg:order-2">{directionsLocationSection}</div>
            <div className="order-2 lg:order-1">{aboutDispensarySection}</div>
            <div className="order-3">{localDealsSection}</div>
            <div className="order-4">{customerReviewsSection}</div>
            <div className="order-5">{postReviewsContentSection}</div>
          <div id="faqs" className="order-6 w-full px-4 lg:px-0">
            <FaqsSection
              mapEmbed={mapEmbed}
              seoTitle={seoTitle}
              seoText={seoText}
              faqs={faqs}
              locationName={locationName}
              includeSchema={false}
            />
          </div>
          </div>

          {/* Sidebar is desktop-only chrome — nav and CTAs, no body copy, so
              hiding it costs a crawler nothing. */}
          <div className="hidden lg:block">
            <div className="sticky top-24">{desktopSidebarSection}</div>
          </div>
        </div>
      </div>

      {nearbyDispensariesSection}
    </>
  );
}

// Error boundary for handling 404 and other errors
export function ErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    return (
      <div className="flex min-h-[calc(100vh-80px)] items-center justify-center bg-[#F8FAFC] px-4 py-12">
        <div className="mx-auto max-w-2xl text-center">
          {/* 404 Title */}
          <h1 className="mb-6 text-6xl font-bold text-[#264B9A]">
            4<span className="text-[#FFD100]">0</span>4
          </h1>

          {/* Error Message */}
          <div className="mb-8 space-y-4">
            <h2 className="font-poppins text-2xl font-semibold text-[#1E293B]">
              {error.status === 404 ? 'Dispensary Location Not Found' : 'Something Went Wrong'}
            </h2>
            <p className="font-helvetica-neue text-lg leading-6 text-[#4A5565]">
              {error.status === 404
                ? "We couldn't find information for this dispensary location. This page may be for a location that doesn't exist or has been moved."
                : "We're having trouble loading this dispensary information. Please try again later."}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/dispensary-locations"
              className="font-helvetica-neue flex items-center justify-center gap-2 rounded-lg bg-[#264B9A] px-8 py-3 text-base font-semibold text-white transition-all duration-300 hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="h-4 w-4">
                <path
                  d="M8 1C6.61553 1 5.26216 1.41054 4.11101 2.17971C2.95987 2.94888 2.06266 4.04213 1.53285 5.32122C1.00303 6.6003 0.86441 8.00776 1.13451 9.36563C1.4046 10.7235 2.07129 11.9708 3.05026 12.9497C4.02922 13.9287 5.2765 14.5954 6.63437 14.8655C7.99224 15.1356 9.3997 14.997 10.6788 14.4672C11.9579 13.9373 13.0511 13.0401 13.8203 11.889C14.5895 10.7378 15 9.38447 15 8C15 6.14348 14.2625 4.36301 12.9497 3.05025C11.637 1.7375 9.85652 1 8 1Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 5V8L10 10"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Find Our Locations
            </Link>
            <Link
              to="/shop"
              className="font-helvetica-neue flex items-center justify-center gap-2 rounded-lg border-2 border-[#264B9A] px-8 py-3 text-base font-semibold text-[#264B9A] transition-all duration-300 hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="h-4 w-4">
                <path
                  d="M11 6L7 10L5 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle
                  cx="8"
                  cy="8"
                  r="7"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Shop Products
            </Link>
          </div>

          {/* Additional Help Text */}
          <div className="mt-8 rounded-lg bg-white p-6 shadow-sm">
            <h3 className="font-poppins mb-3 text-lg font-semibold text-[#1E293B]">
              Looking for a specific location?
            </h3>
            <p className="font-helvetica-neue mb-4 text-base leading-6 text-[#4A5565]">
              We have dispensaries throughout California. Visit our locations page to find the one
              nearest you.
            </p>
            <Link
              to="/"
              className="font-helvetica-neue inline-flex items-center gap-2 text-base font-medium text-[#264B9A] hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="h-4 w-4">
                <path
                  d="M6.5 2L2 6.5L6.5 11"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2 6.5H14"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Handle non-HTTP errors (500, etc.)
  return (
    <div className="flex min-h-[calc(100vh-80px)] items-center justify-center bg-[#F8FAFC] px-4 py-12">
      <div className="mx-auto max-w-2xl text-center">
        <h1 className="mb-6 text-6xl font-bold text-[#264B9A]">
          5<span className="text-[#FFD100]">0</span>0
        </h1>
        <div className="mb-8 space-y-4">
          <h2 className="font-poppins text-2xl font-semibold text-[#1E293B]">Server Error</h2>
          <p className="font-helvetica-neue text-lg leading-6 text-[#4A5565]">
            We're experiencing technical difficulties. Please try again later.
          </p>
        </div>
        <Link
          to="/"
          className="font-helvetica-neue inline-flex items-center gap-2 rounded-lg bg-[#264B9A] px-8 py-3 text-base font-semibold text-white transition-all duration-300 hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
}
